import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, ClipboardList, Camera, X,
} from "lucide-react";

const ROLE_OPTIONS = [
  { key: "admin", label: "Admin" },
  { key: "service", label: "Service" },
  { key: "vask", label: "Renhold (Vask)" },
];

function TypeEditor({ open, onOpenChange, initial, onSaved }) {
  const isNew = !initial;
  const [labelNo, setLabelNo] = useState("");
  const [labelEn, setLabelEn] = useState("");
  const [description, setDescription] = useState("");
  const [roles, setRoles] = useState(["admin", "service"]);
  const [autoTrigger, setAutoTrigger] = useState(false);
  const [items, setItems] = useState([{ label_no: "", label_en: "", photo_on_fail: false }]);

  useEffect(() => {
    if (open) {
      setLabelNo(initial?.label_no || "");
      setLabelEn(initial?.label_en || "");
      setDescription(initial?.description || "");
      setRoles(initial?.roles || ["admin", "service"]);
      setAutoTrigger(initial?.auto_trigger_on_return || false);
      setItems(
        initial?.items?.length
          ? initial.items.map((i) => ({
              key: i.key,
              label_no: i.label_no || "",
              label_en: i.label_en || "",
              photo_on_fail: !!i.photo_on_fail,
            }))
          : [{ label_no: "", label_en: "", photo_on_fail: false }],
      );
    }
  }, [open, initial]);

  const setItem = (idx, patch) =>
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((arr) => [...arr, { label_no: "", label_en: "", photo_on_fail: false }]);
  const removeItem = (idx) =>
    setItems((arr) => arr.filter((_, i) => i !== idx));
  const toggleRole = (r) =>
    setRoles((arr) => (arr.includes(r) ? arr.filter((x) => x !== r) : [...arr, r]));

  const save = async () => {
    const trimmed = labelNo.trim();
    if (!trimmed) return toast.error("Skriv inn et navn");
    if (roles.length === 0) return toast.error("Velg minst én rolle som kan utføre");
    const cleanItems = items
      .map((i) => ({
        key: i.key,
        label_no: (i.label_no || "").trim(),
        label_en: (i.label_en || "").trim() || undefined,
        photo_on_fail: !!i.photo_on_fail,
      }))
      .filter((i) => i.label_no);
    if (cleanItems.length === 0) return toast.error("Legg til minst ett punkt");
    const body = {
      label_no: trimmed,
      label_en: labelEn.trim() || undefined,
      description: description.trim(),
      roles,
      auto_trigger_on_return: autoTrigger,
      items: cleanItems,
    };
    try {
      if (isNew) {
        await api.post("/checklist-types", body);
        toast.success("Sjekkliste-type opprettet");
      } else {
        await api.patch(`/checklist-types/${initial.key}`, body);
        toast.success("Sjekkliste-type oppdatert");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "Ny sjekkliste-type" : `Rediger «${initial?.label_no}»`}
          </DialogTitle>
          <DialogDescription>
            Definer en helt egen sjekkliste-type, f.eks. «Vinterklargjøring» eller
            «Årlig service», med egne punkter og hvem som kan utføre den.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Navn (norsk)</Label>
              <Input
                value={labelNo}
                onChange={(e) => setLabelNo(e.target.value)}
                data-testid="type-editor-label-no"
                placeholder="F.eks. Vinterklargjøring"
              />
            </div>
            <div>
              <Label>Navn (engelsk, valgfritt)</Label>
              <Input
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
                data-testid="type-editor-label-en"
                placeholder="E.g. Winter prep"
              />
            </div>
          </div>
          <div>
            <Label>Beskrivelse (valgfritt)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Kort om når denne sjekklisten brukes."
              data-testid="type-editor-description"
            />
          </div>

          <div>
            <Label>Hvilke roller kan utføre?</Label>
            <div className="flex gap-3 flex-wrap mt-1.5">
              {ROLE_OPTIONS.map((r) => (
                <label
                  key={r.key}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 cursor-pointer"
                  data-testid={`type-editor-role-${r.key}`}
                >
                  <Checkbox
                    checked={roles.includes(r.key)}
                    onCheckedChange={() => toggleRole(r.key)}
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <label
            className="flex items-start gap-2.5 p-3 rounded-md border border-[#1DA3B3]/30 bg-[#1DA3B3]/5 cursor-pointer"
            data-testid="type-editor-auto-trigger"
          >
            <Checkbox
              checked={autoTrigger}
              onCheckedChange={(v) => setAutoTrigger(!!v)}
            />
            <div>
              <div className="text-sm font-medium text-[#0C1824]">
                Auto-opprett ved retur (ute → inne)
              </div>
              <div className="text-xs text-[#2B3F4D]">
                Sjekklisten genereres automatisk når en båt kommer inn igjen
                etter en booking. Bare relevant for typer som «Utsjekk» eller
                «Renhold». Båten må også ha typen huket av i sin egen liste.
              </div>
            </div>
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Punkter</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addItem}
                data-testid="type-editor-add-item"
                className="border-[#BD984D] text-[#A57F39] h-8"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Legg til
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_auto_auto] gap-2 items-center p-2 border border-slate-200 rounded-md"
                  data-testid={`type-editor-item-${idx}`}
                >
                  <Input
                    value={it.label_no}
                    onChange={(e) => setItem(idx, { label_no: e.target.value })}
                    placeholder="F.eks. Tøm vanntank"
                    data-testid={`type-editor-item-${idx}-label`}
                  />
                  <label className="flex items-center gap-1.5 text-xs text-[#2B3F4D] whitespace-nowrap px-2">
                    <Checkbox
                      checked={it.photo_on_fail}
                      onCheckedChange={(v) => setItem(idx, { photo_on_fail: !!v })}
                      data-testid={`type-editor-item-${idx}-photo`}
                    />
                    <Camera className="w-3 h-3" /> Bilde ved avvik
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeItem(idx)}
                    className="h-9 px-2 border-red-200 text-red-600 hover:bg-red-50"
                    data-testid={`type-editor-item-${idx}-remove`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button
            onClick={save}
            data-testid="type-editor-save"
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            {isNew ? "Opprett type" : "Lagre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ChecklistTypesManager() {
  const [types, setTypes] = useState([]);
  const [editor, setEditor] = useState({ open: false, type: null });

  const load = async () => {
    const r = await api.get("/checklist-types");
    setTypes(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(
      `Slette sjekkliste-typen «${t.label_no}»? Dette kan kun gjøres hvis typen ikke er i bruk.`,
    )) return;
    try {
      await api.delete(`/checklist-types/${t.key}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const builtin = types.filter((t) => t.is_builtin);
  const custom = types.filter((t) => !t.is_builtin);

  return (
    <div className="space-y-4" data-testid="types-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#2B3F4D] max-w-2xl">
          Innebygde typer (renhold, innsjekk, utsjekk) kan redigeres via
          «Sjekklister»-fanen ved siden av. Her oppretter du dine helt egne
          sjekkliste-typer for spesialjobber.
        </p>
        <Button
          onClick={() => setEditor({ open: true, type: null })}
          data-testid="types-add"
          className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Ny sjekkliste-type
        </Button>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-2">
          Egne typer
        </div>
        <div className="space-y-2">
          {custom.length === 0 && (
            <Card className="p-6 text-center text-sm text-[#2B3F4D]">
              Du har ikke laget egne typer ennå.
            </Card>
          )}
          {custom.map((t) => (
            <Card key={t.key} className="p-4" data-testid={`types-row-${t.key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0C1824] flex items-center gap-2 flex-wrap">
                    <ClipboardList className="w-4 h-4 text-[#1DA3B3]" />
                    {t.label_no}
                    <Badge className="bg-[#BD984D]/15 text-[#A57F39] text-[10px]">EGEN</Badge>
                    {t.auto_trigger_on_return && (
                      <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] text-[10px]">Auto ved retur</Badge>
                    )}
                  </div>
                  {t.description && (
                    <div className="text-xs text-[#2B3F4D] mt-1">{t.description}</div>
                  )}
                  <div className="text-[11px] text-[#2B3F4D] mt-1">
                    {t.items.length} punkter · Roller: {(t.roles || []).join(", ") || "—"}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditor({ open: true, type: t })}
                    data-testid={`types-edit-${t.key}`}
                    className="border-[#0C1824] text-[#0C1824] h-9"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => remove(t)}
                    data-testid={`types-delete-${t.key}`}
                    className="border-red-200 text-red-600 hover:bg-red-50 h-9"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-2">
          Innebygde typer
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {builtin.map((t) => (
            <Card
              key={t.key}
              className="p-3"
              data-testid={`types-builtin-${t.key}`}
            >
              <div className="font-semibold text-[#0C1824] text-sm">{t.label_no}</div>
              <div className="text-[11px] text-[#2B3F4D] mt-1">
                {t.items.length} punkter · {(t.roles || []).join(", ")}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <TypeEditor
        open={editor.open}
        onOpenChange={(o) => setEditor({ open: o, type: editor.type })}
        initial={editor.type}
        onSaved={load}
      />
    </div>
  );
}
